import React, { useEffect, useMemo, useState } from 'react';
import { CVData, Experience, Education, Skill, Language, Interest, Reference, TEMPLATE_CONFIGS } from '../../types/cv';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { User, Briefcase, GraduationCap, Plus, Trash2, X, Upload, Award, Globe, Heart, Users, Palette, ChevronLeft, ChevronRight, Minus, PenLine, Loader2 } from 'lucide-react';
import { createItemId } from '../../utils/cvPagination';
import { compressImageFile } from '../../utils/compressImage';
import { MAGENTA_HOBBY_MAX, MAGENTA_HOBBY_OPTIONS } from './templates/magentaRibbonHobbies';
import cvService from '../../services/cvService';
import { trackEvent } from '../../utils/analytics';
import {
    FONT_SIZE_MAX,
    FONT_SIZE_MIN,
    FONT_SIZE_PARTS,
    FONT_SIZE_STEP,
    clampFontSize,
    getFontSize,
} from '../../utils/cvFontSizes';

type StepId =
    | 'personal'
    | 'experience'
    | 'education'
    | 'skills'
    | 'languages'
    | 'interests'
    | 'references'
    | 'style';

interface CVFormProps {
    data: CVData;
    onChange: (section: keyof CVData, value: any) => void;
    template: string;
    /** Étape forcée par le tutoriel guidé */
    tourFocusStep?: StepId | null;
}

const CVForm: React.FC<CVFormProps> = ({ data, onChange, template, tourFocusStep = null }) => {
    const [showExperienceForm, setShowExperienceForm] = useState(false);
    const [showEducationForm, setShowEducationForm] = useState(false);
    const [editingExperienceIndex, setEditingExperienceIndex] = useState<number | null>(null);
    const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null);
    const [stepIndex, setStepIndex] = useState(0);

    const emptyExperience = (): Experience => ({
        id: createItemId('exp'),
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        description: '',
        isCurrent: false
    });

    const emptyEducation = (): Education => ({
        id: createItemId('edu'),
        institution: '',
        degree: '',
        field: '',
        startDate: '',
        endDate: '',
        description: '',
        isCurrent: false
    });

    const [experienceForm, setExperienceForm] = useState<Experience>(emptyExperience);
    const [educationForm, setEducationForm] = useState<Education>(emptyEducation);
    const [generating, setGenerating] = useState<'summary' | 'experience' | 'education' | null>(null);
    const [generateError, setGenerateError] = useState('');

    const runGenerate = async (
        type: 'summary' | 'experience' | 'education',
        apply: (text: string) => void
    ) => {
        setGenerating(type);
        setGenerateError('');
        try {
            const context: Record<string, unknown> = {
                personalInfo: data.personalInfo,
                skills: data.skills,
                experience: data.experience,
            };
            if (type === 'summary') {
                context.draft = data.personalInfo.summary || '';
            } else if (type === 'experience') {
                context.experienceItem = experienceForm;
                context.draft = experienceForm.description || '';
            } else {
                context.educationItem = educationForm;
                context.draft = educationForm.description || '';
            }
            const { text } = await cvService.generateDescription(type, context);
            if (text?.trim()) apply(text.trim());
            else setGenerateError('Aucun texte généré. Réessayez.');
        } catch (e: unknown) {
            setGenerateError(e instanceof Error ? e.message : 'Génération impossible');
        } finally {
            setGenerating(null);
        }
    };

    const templateConfig = TEMPLATE_CONFIGS.find(t => t.id === template);

    /** Ordre mobile : contenu d’abord, personnalisation en dernier */
    const steps = useMemo(() => {
        const list: { id: StepId; label: string }[] = [
            { id: 'personal', label: 'Infos personnelles' },
            { id: 'experience', label: 'Expériences' },
            { id: 'education', label: 'Formations' },
        ];
        if (templateConfig?.fields.skills) list.push({ id: 'skills', label: 'Compétences' });
        if (templateConfig?.fields.languages) list.push({ id: 'languages', label: 'Langues' });
        if (templateConfig?.fields.interests) list.push({ id: 'interests', label: "Centres d'intérêt" });
        if (templateConfig?.fields.references) list.push({ id: 'references', label: 'Références' });
        list.push({ id: 'style', label: 'Personnalisation' });
        return list;
    }, [templateConfig]);

    useEffect(() => {
        setStepIndex((i) => Math.min(i, Math.max(0, steps.length - 1)));
    }, [steps.length]);

    useEffect(() => {
        setStepIndex(0);
    }, [template]);

    useEffect(() => {
        if (!tourFocusStep) return;
        const idx = steps.findIndex((s) => s.id === tourFocusStep);
        if (idx >= 0) {
            setStepIndex(idx);
            return;
        }
        // Si l’étape n’existe pas pour ce modèle (ex. skills), viser la 1re section dispo proche
        if (tourFocusStep === 'skills') {
            const fallback = steps.findIndex((s) =>
                ['skills', 'languages', 'interests', 'references', 'style'].includes(s.id)
            );
            if (fallback >= 0) setStepIndex(fallback);
        }
    }, [tourFocusStep, steps]);

    const currentStep = steps[stepIndex]?.id ?? 'personal';
    const isFirst = stepIndex === 0;
    const isLast = stepIndex >= steps.length - 1;

    const stepClass = (id: StepId) =>
        `md:!block ${currentStep === id ? 'block' : 'hidden'}`;

    const goNext = () => {
        if (!isLast) {
            // Ce stepper (goNext/goPrev) n'existe qu'en mobile — sur desktop
            // toutes les sections sont visibles simultanément (voir stepClass
            // ci-dessus). context: 'mobile_stepper' le rend explicite dans les
            // rapports pour ne pas comparer ces chiffres à du trafic desktop.
            trackEvent('cv_section_completed', { section: currentStep, context: 'mobile_stepper' });
            setStepIndex((i) => i + 1);
            const scroller = document.querySelector('.overflow-y-auto');
            if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goPrev = () => {
        if (!isFirst) {
            setStepIndex((i) => i - 1);
            const scroller = document.querySelector('.overflow-y-auto');
            if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        onChange('personalInfo', { ...data.personalInfo, [name]: value });
    };

    const handleStyleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        onChange('style', { ...data.style, [name]: value });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const profileImage = await compressImageFile(file);
            onChange('personalInfo', { ...data.personalInfo, profileImage });
        } catch {
            const reader = new FileReader();
            reader.onloadend = () => {
                onChange('personalInfo', {
                    ...data.personalInfo,
                    profileImage: reader.result as string,
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddExperience = () => {
        if (editingExperienceIndex !== null) {
            const updated = [...data.experience];
            updated[editingExperienceIndex] = experienceForm;
            onChange('experience', updated);
            setEditingExperienceIndex(null);
        } else {
            onChange('experience', [...data.experience, { ...experienceForm, id: experienceForm.id || createItemId('exp') }]);
        }
        setExperienceForm(emptyExperience());
        setShowExperienceForm(false);
    };

    const handleEditExperience = (index: number) => {
        setExperienceForm(data.experience[index]);
        setEditingExperienceIndex(index);
        setShowExperienceForm(true);
    };

    const handleDeleteExperience = (index: number) => {
        onChange('experience', data.experience.filter((_, i) => i !== index));
    };

    const handleAddEducation = () => {
        if (editingEducationIndex !== null) {
            const updated = [...data.education];
            updated[editingEducationIndex] = educationForm;
            onChange('education', updated);
            setEditingEducationIndex(null);
        } else {
            onChange('education', [...data.education, { ...educationForm, id: educationForm.id || createItemId('edu') }]);
        }
        setEducationForm(emptyEducation());
        setShowEducationForm(false);
    };

    const handleEditEducation = (index: number) => {
        setEducationForm(data.education[index]);
        setEditingEducationIndex(index);
        setShowEducationForm(true);
    };

    const handleDeleteEducation = (index: number) => {
        onChange('education', data.education.filter((_, i) => i !== index));
    };

    const handleAddSkill = () => {
        const newSkill: Skill = { id: createItemId('skill'), name: '', level: 50 };
        onChange('skills', [...(data.skills || []), newSkill]);
    };

    const handleSkillChange = (index: number, field: keyof Skill, value: any) => {
        const updated = [...(data.skills || [])];
        updated[index] = { ...updated[index], [field]: value };
        onChange('skills', updated);
    };

    const handleDeleteSkill = (index: number) => {
        onChange('skills', (data.skills || []).filter((_, i) => i !== index));
    };

    const handleAddLanguage = () => {
        const newLang: Language = { id: createItemId('lang'), name: '', level: 50 };
        onChange('languages', [...(data.languages || []), newLang]);
    };

    const handleLanguageChange = (index: number, field: keyof Language, value: any) => {
        const updated = [...(data.languages || [])];
        updated[index] = { ...updated[index], [field]: value };
        onChange('languages', updated);
    };

    const handleDeleteLanguage = (index: number) => {
        onChange('languages', (data.languages || []).filter((_, i) => i !== index));
    };

    const handleAddInterest = () => {
        const newInterest: Interest = { id: createItemId('int'), name: '' };
        onChange('interests', [...(data.interests || []), newInterest]);
    };

    const handleInterestChange = (index: number, value: string) => {
        const updated = [...(data.interests || [])];
        updated[index] = { ...updated[index], name: value };
        onChange('interests', updated);
    };

    const handleDeleteInterest = (index: number) => {
        onChange('interests', (data.interests || []).filter((_, i) => i !== index));
    };

    const isIconInterests = template === 'magenta-ribbon';

    const toggleMagentaHobby = (hobbyId: string) => {
        const current = data.interests || [];
        const existingIndex = current.findIndex((i) => i.name === hobbyId);
        if (existingIndex >= 0) {
            onChange(
                'interests',
                current.filter((_, i) => i !== existingIndex)
            );
            return;
        }
        if (current.length >= MAGENTA_HOBBY_MAX) return;
        onChange('interests', [...current, { id: createItemId('int'), name: hobbyId }]);
    };

    const handleAddReference = () => {
        const newRef: Reference = {
            id: createItemId('ref'),
            name: '',
            position: '',
            company: '',
            phone: '',
            email: '',
        };
        onChange('references', [...(data.references || []), newRef]);
    };

    const handleReferenceChange = (index: number, field: keyof Reference, value: string) => {
        const updated = [...(data.references || [])];
        updated[index] = { ...updated[index], [field]: value };
        onChange('references', updated);
    };

    const handleDeleteReference = (index: number) => {
        onChange('references', (data.references || []).filter((_, i) => i !== index));
    };

    const sectionPad = 'p-5 sm:p-6 md:p-8';

    return (
        <div className="space-y-6 md:space-y-10 pb-24 md:pb-0">
            {/* Indicateur d’étapes — mobile uniquement */}
            <div className="md:hidden sticky top-0 z-10 -mx-1 px-1 pb-3 bg-slate-50/95 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Étape {stepIndex + 1} / {steps.length}
                    </span>
                    <span className="text-xs font-semibold text-indigo-700 truncate max-w-[60%] text-right">
                        {steps[stepIndex]?.label}
                    </span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                        style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
                    />
                </div>
            </div>

            {/* Personnalisation — visible en 1er sur desktop ; dernière étape mobile */}
            <section
                data-tour="style"
                className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('style')}`}
            >
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
                    <div className="p-2 bg-pink-50 rounded-lg">
                        <Palette className="w-5 h-5 text-pink-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">Personnalisation</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-tour="style-colors">
                    <div>
                        <label className="text-sm font-semibold text-slate-700 block mb-2">Couleur Principale</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                name="primaryColor"
                                value={data.style?.primaryColor || templateConfig?.hex || '#000000'}
                                onChange={handleStyleChange}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200 p-1"
                            />
                            <span className="text-xs text-slate-500 font-mono">{data.style?.primaryColor || 'Défaut'}</span>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-slate-700 block mb-2">Couleur du Texte</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                name="textColor"
                                value={data.style?.textColor || '#1e293b'}
                                onChange={handleStyleChange}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200 p-1"
                            />
                            <span className="text-xs text-slate-500 font-mono">{data.style?.textColor || 'Défaut'}</span>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-slate-700 block mb-2">Arrière-plan Page</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                name="backgroundColor"
                                value={data.style?.backgroundColor || '#ffffff'}
                                onChange={handleStyleChange}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200 p-1"
                            />
                            <span className="text-xs text-slate-500 font-mono">{data.style?.backgroundColor || 'Blanc'}</span>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-slate-700 block mb-2">Arrière-plan Section</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                name="sectionBackgroundColor"
                                value={data.style?.sectionBackgroundColor || '#f8fafc'}
                                onChange={handleStyleChange}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200 p-1"
                            />
                            <span className="text-xs text-slate-500 font-mono">{data.style?.sectionBackgroundColor || 'Gris'}</span>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-slate-700 block mb-2">Bordure Photo</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                name="imageBorderColor"
                                value={data.style?.imageBorderColor || '#ffffff'}
                                onChange={handleStyleChange}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200 p-1"
                            />
                            <span className="text-xs text-slate-500 font-mono">{data.style?.imageBorderColor || 'Blanc'}</span>
                        </div>
                    </div>
                    <div data-tour="style-font">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">Police d'écriture</label>
                        <select
                            name="fontFamily"
                            value={data.style?.fontFamily || 'font-sans'}
                            onChange={(e) => onChange('style', { ...data.style, fontFamily: e.target.value })}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                        >
                            <option value="font-sans">Sans Serif (Défaut)</option>
                            <option value="font-serif">Serif</option>
                            <option value="font-mono">Monospace</option>
                            <option value="font-montserrat">Montserrat</option>
                            <option value="font-poppins">Poppins</option>
                            <option value="font-lato">Lato</option>
                            <option value="font-source-sans">Source Sans</option>
                            <option value="font-raleway">Raleway</option>
                        </select>
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100" data-tour="style-sizes">
                    <h4 className="text-sm font-bold text-slate-800 mb-1">Taille des textes</h4>
                    <p className="text-xs text-slate-500 mb-4">
                        Augmentez ou diminuez la taille de chaque partie du CV.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {FONT_SIZE_PARTS.map(({ key, label }) => {
                            const value = getFontSize(data.style, key);
                            return (
                                <div
                                    key={key}
                                    className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5"
                                >
                                    <span className="text-sm font-semibold text-slate-700 truncate">{label}</span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button
                                            type="button"
                                            aria-label={`Diminuer ${label}`}
                                            disabled={value <= FONT_SIZE_MIN}
                                            onClick={() =>
                                                onChange('style', {
                                                    ...data.style,
                                                    fontSizes: {
                                                        ...data.style?.fontSizes,
                                                        [key]: clampFontSize(value - FONT_SIZE_STEP),
                                                    },
                                                })
                                            }
                                            className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <span className="w-12 text-center text-xs font-bold font-mono text-slate-600">
                                            {value}%
                                        </span>
                                        <button
                                            type="button"
                                            aria-label={`Augmenter ${label}`}
                                            disabled={value >= FONT_SIZE_MAX}
                                            onClick={() =>
                                                onChange('style', {
                                                    ...data.style,
                                                    fontSizes: {
                                                        ...data.style?.fontSizes,
                                                        [key]: clampFontSize(value + FONT_SIZE_STEP),
                                                    },
                                                })
                                            }
                                            className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section
                data-tour="personal"
                className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('personal')}`}
            >
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                        <User className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">Informations Personnelles</h3>
                </div>

                <div className="mb-6 flex justify-center">
                    <div className="relative">
                        <label htmlFor="profile-image" className="cursor-pointer group">
                            <div className="w-32 h-32 rounded-full border-4 border-indigo-100 overflow-hidden bg-slate-100 flex items-center justify-center hover:border-indigo-300 transition-all">
                                {data.personalInfo.profileImage ? (
                                    <img src={data.personalInfo.profileImage} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="flex flex-col items-center text-slate-400 group-hover:text-indigo-500 transition-colors">
                                        <Upload className="w-8 h-8 mb-2" />
                                        <span className="text-xs font-bold">Photo</span>
                                    </div>
                                )}
                            </div>
                            <div className="absolute bottom-0 right-0 p-2 bg-indigo-600 rounded-full text-white shadow-lg hover:bg-indigo-700 transition-colors">
                                <Upload className="w-4 h-4" />
                            </div>
                        </label>
                        <input
                            id="profile-image"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageUpload}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input label="Prénom" name="firstName" placeholder="Jean" value={data.personalInfo.firstName} onChange={handlePersonalInfoChange} />
                    <Input label="Nom" name="lastName" placeholder="Dupont" value={data.personalInfo.lastName} onChange={handlePersonalInfoChange} />
                    <Input label="Email professionnel" name="email" type="email" placeholder="jean.dupont@exemple.com" value={data.personalInfo.email} onChange={handlePersonalInfoChange} />
                    <Input label="Téléphone" name="phone" placeholder="+225 01 02 03 04 05" value={data.personalInfo.phone} onChange={handlePersonalInfoChange} />
                    {templateConfig?.fields.website && (
                        <Input label="Site web" name="website" placeholder="www.monsite.com" value={data.personalInfo.website || ''} onChange={handlePersonalInfoChange} />
                    )}
                    <Input label="Adresse" name="address" placeholder="123 Rue Principale" value={data.personalInfo.address} onChange={handlePersonalInfoChange} />
                    <Input label="Profession" name="profession" placeholder="Développeur Fullstack" className="md:col-span-2" value={data.personalInfo.profession} onChange={handlePersonalInfoChange} />
                    <div className="md:col-span-2 animate-fade-in">
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <label className="text-sm font-semibold text-slate-700 ml-1">Résumé professionnel</label>
                            <button
                                type="button"
                                disabled={generating === 'summary'}
                                onClick={() =>
                                    runGenerate('summary', (text) =>
                                        onChange('personalInfo', { ...data.personalInfo, summary: text })
                                    )
                                }
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50 px-2 py-1 rounded-lg hover:bg-indigo-50"
                            >
                                {generating === 'summary' ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <PenLine className="w-3.5 h-3.5" />
                                )}
                                {data.personalInfo.summary?.trim() ? 'Reformuler' : 'Rédiger'}
                            </button>
                        </div>
                        <textarea
                            name="summary"
                            rows={4}
                            placeholder="Décrivez brièvement votre parcours et vos aspirations..."
                            className="input-premium mt-1 resize-none"
                            value={data.personalInfo.summary}
                            onChange={handlePersonalInfoChange}
                        />
                        <p className="text-[10px] text-slate-400 mt-2 ml-1">
                            Ce texte apparaîtra en haut de votre CV. Remplissez d’abord votre profession pour un meilleur résultat.
                        </p>
                        {generateError && (
                            <p className="text-xs text-red-600 mt-1 ml-1">{generateError}</p>
                        )}
                    </div>
                </div>
            </section>

            <section
                data-tour="experience"
                className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('experience')}`}
            >
                <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4 gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-blue-50 rounded-lg shrink-0">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-slate-900 truncate">Expériences Professionnelles</h3>
                    </div>
                    <Button onClick={() => setShowExperienceForm(!showExperienceForm)} className="h-9 px-3 md:px-4 text-xs shrink-0">
                        <Plus className="w-4 h-4 md:mr-2" />
                        <span className="hidden md:inline">Ajouter</span>
                    </Button>
                </div>

                {showExperienceForm && (
                    <div className="mb-6 p-4 md:p-6 bg-blue-50/30 rounded-xl border-2 border-blue-100">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="font-bold text-slate-700">
                                {editingExperienceIndex !== null ? 'Modifier l\'expérience' : 'Nouvelle expérience'}
                            </h4>
                            <button type="button" onClick={() => {
                                setShowExperienceForm(false);
                                setEditingExperienceIndex(null);
                                setExperienceForm(emptyExperience());
                            }} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input label="Entreprise" placeholder="Google Inc." value={experienceForm.company} onChange={(e) => setExperienceForm({ ...experienceForm, company: e.target.value })} />
                            <Input label="Poste" placeholder="Développeur Senior" value={experienceForm.position} onChange={(e) => setExperienceForm({ ...experienceForm, position: e.target.value })} />
                            <Input label="Date de début" type="date" value={experienceForm.startDate} onChange={(e) => setExperienceForm({ ...experienceForm, startDate: e.target.value })} />
                            <Input label="Date de fin" type="date" value={experienceForm.endDate} disabled={experienceForm.isCurrent} onChange={(e) => setExperienceForm({ ...experienceForm, endDate: e.target.value })} />
                            <div className="md:col-span-2">
                                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                                    <input type="checkbox" checked={experienceForm.isCurrent} onChange={(e) => setExperienceForm({ ...experienceForm, isCurrent: e.target.checked, endDate: e.target.checked ? '' : experienceForm.endDate })} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                                    Poste actuel
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <label className="text-sm font-semibold text-slate-700 ml-1">Description</label>
                                    <button
                                        type="button"
                                        disabled={generating === 'experience'}
                                        onClick={() =>
                                            runGenerate('experience', (text) =>
                                                setExperienceForm((prev) => ({ ...prev, description: text }))
                                            )
                                        }
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50 px-2 py-1 rounded-lg hover:bg-indigo-50"
                                    >
                                        {generating === 'experience' ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <PenLine className="w-3.5 h-3.5" />
                                        )}
                                        {experienceForm.description?.trim() ? 'Reformuler' : 'Rédiger'}
                                    </button>
                                </div>
                                <textarea rows={3} placeholder="Décrivez vos responsabilités et réalisations..." className="input-premium mt-1 resize-none" value={experienceForm.description} onChange={(e) => setExperienceForm({ ...experienceForm, description: e.target.value })} />
                                {generateError && (
                                    <p className="text-xs text-red-600 mt-1 ml-1">{generateError}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-4">
                            <Button variant="outline" onClick={() => { setShowExperienceForm(false); setEditingExperienceIndex(null); setExperienceForm(emptyExperience()); }}>Annuler</Button>
                            <Button onClick={handleAddExperience}>{editingExperienceIndex !== null ? 'Mettre à jour' : 'Ajouter'}</Button>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {data.experience.map((exp, index) => (
                        <div key={exp.id || index} className="p-4 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-slate-900">{exp.position}</h4>
                                    <p className="text-sm text-indigo-600 font-semibold">{exp.company}</p>
                                    <p className="text-xs text-slate-400 mt-1">{exp.startDate} - {exp.isCurrent ? 'Présent' : exp.endDate}</p>
                                    {exp.description && <p className="text-xs text-slate-600 mt-2">{exp.description}</p>}
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button type="button" onClick={() => handleEditExperience(index)} className="text-blue-500 hover:text-blue-700">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    </button>
                                    <button type="button" onClick={() => handleDeleteExperience(index)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {data.experience.length === 0 && !showExperienceForm && (
                        <p className="text-center text-slate-400 italic py-8">Aucune expérience ajoutée</p>
                    )}
                </div>
            </section>

            <section
                data-tour="education"
                className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('education')}`}
            >
                <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4 gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-emerald-50 rounded-lg shrink-0">
                            <GraduationCap className="w-5 h-5 text-emerald-600" />
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-slate-900">Formations</h3>
                    </div>
                    <Button onClick={() => setShowEducationForm(!showEducationForm)} className="h-9 px-3 md:px-4 text-xs shrink-0">
                        <Plus className="w-4 h-4 md:mr-2" />
                        <span className="hidden md:inline">Ajouter</span>
                    </Button>
                </div>

                {showEducationForm && (
                    <div className="mb-6 p-4 md:p-6 bg-emerald-50/30 rounded-xl border-2 border-emerald-100">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="font-bold text-slate-700">{editingEducationIndex !== null ? 'Modifier la formation' : 'Nouvelle formation'}</h4>
                            <button type="button" onClick={() => { setShowEducationForm(false); setEditingEducationIndex(null); setEducationForm(emptyEducation()); }} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input label="Établissement" placeholder="Université de Paris" value={educationForm.institution} onChange={(e) => setEducationForm({ ...educationForm, institution: e.target.value })} />
                            <Input label="Diplôme" placeholder="Master" value={educationForm.degree} onChange={(e) => setEducationForm({ ...educationForm, degree: e.target.value })} />
                            <Input label="Domaine d'études" placeholder="Informatique" className="md:col-span-2" value={educationForm.field} onChange={(e) => setEducationForm({ ...educationForm, field: e.target.value })} />
                            <Input label="Date de début" type="date" value={educationForm.startDate} onChange={(e) => setEducationForm({ ...educationForm, startDate: e.target.value })} />
                            <Input label="Date de fin" type="date" value={educationForm.endDate} disabled={educationForm.isCurrent} onChange={(e) => setEducationForm({ ...educationForm, endDate: e.target.value })} />
                            <div className="md:col-span-2">
                                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                                    <input type="checkbox" checked={educationForm.isCurrent} onChange={(e) => setEducationForm({ ...educationForm, isCurrent: e.target.checked, endDate: e.target.checked ? '' : educationForm.endDate })} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                                    Formation en cours
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <label className="text-sm font-semibold text-slate-700 ml-1">Description</label>
                                    <button
                                        type="button"
                                        disabled={generating === 'education'}
                                        onClick={() =>
                                            runGenerate('education', (text) =>
                                                setEducationForm((prev) => ({ ...prev, description: text }))
                                            )
                                        }
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50 px-2 py-1 rounded-lg hover:bg-indigo-50"
                                    >
                                        {generating === 'education' ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <PenLine className="w-3.5 h-3.5" />
                                        )}
                                        {educationForm.description?.trim() ? 'Reformuler' : 'Rédiger'}
                                    </button>
                                </div>
                                <textarea rows={3} placeholder="Décrivez votre parcours et vos réalisations..." className="input-premium mt-1 resize-none" value={educationForm.description} onChange={(e) => setEducationForm({ ...educationForm, description: e.target.value })} />
                                {generateError && (
                                    <p className="text-xs text-red-600 mt-1 ml-1">{generateError}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-4">
                            <Button variant="outline" onClick={() => { setShowEducationForm(false); setEditingEducationIndex(null); setEducationForm(emptyEducation()); }}>Annuler</Button>
                            <Button onClick={handleAddEducation}>{editingEducationIndex !== null ? 'Mettre à jour' : 'Ajouter'}</Button>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {data.education.map((edu, index) => (
                        <div key={edu.id || index} className="p-4 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-slate-900">{edu.degree} en {edu.field}</h4>
                                    <p className="text-sm text-emerald-600 font-semibold">{edu.institution}</p>
                                    <p className="text-xs text-slate-400 mt-1">{edu.startDate} - {edu.isCurrent ? 'En cours' : edu.endDate}</p>
                                    {edu.description && <p className="text-xs text-slate-600 mt-2">{edu.description}</p>}
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button type="button" onClick={() => handleEditEducation(index)} className="text-blue-500 hover:text-blue-700">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    </button>
                                    <button type="button" onClick={() => handleDeleteEducation(index)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {data.education.length === 0 && !showEducationForm && (
                        <p className="text-center text-slate-400 italic py-8">Aucune formation ajoutée</p>
                    )}
                </div>
            </section>

            {templateConfig?.fields.skills && (
                <section
                    data-tour="skills"
                    className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('skills')}`}
                >
                    <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-purple-50 rounded-lg shrink-0">
                                <Award className="w-5 h-5 text-purple-600" />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold text-slate-900">Compétences</h3>
                        </div>
                        <Button onClick={handleAddSkill} className="h-9 px-3 md:px-4 text-xs shrink-0">
                            <Plus className="w-4 h-4 md:mr-2" />
                            <span className="hidden md:inline">Ajouter</span>
                        </Button>
                    </div>
                    <div className="space-y-4">
                        {(data.skills || []).map((skill, index) => (
                            <div key={skill.id || index} className="p-4 bg-white border border-slate-200 rounded-lg">
                                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                                    <Input label="Nom de la compétence" placeholder="JavaScript" value={skill.name} onChange={(e) => handleSkillChange(index, 'name', e.target.value)} className="flex-1" />
                                    <div className="flex-1">
                                        <label className="text-sm font-semibold text-slate-700 ml-1 block mb-2">Niveau ({skill.level}%)</label>
                                        <input type="range" min="0" max="100" value={skill.level} onChange={(e) => handleSkillChange(index, 'level', parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-600" />
                                    </div>
                                    <button type="button" onClick={() => handleDeleteSkill(index)} className="text-red-500 hover:text-red-700 sm:mt-6 self-end sm:self-auto">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {templateConfig?.fields.languages && (
                <section className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('languages')}`}>
                    <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-teal-50 rounded-lg shrink-0">
                                <Globe className="w-5 h-5 text-teal-600" />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold text-slate-900">Langues</h3>
                        </div>
                        <Button onClick={handleAddLanguage} className="h-9 px-3 md:px-4 text-xs shrink-0">
                            <Plus className="w-4 h-4 md:mr-2" />
                            <span className="hidden md:inline">Ajouter</span>
                        </Button>
                    </div>
                    <div className="space-y-4">
                        {(data.languages || []).map((lang, index) => (
                            <div key={lang.id || index} className="p-4 bg-white border border-slate-200 rounded-lg">
                                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                                    <Input label="Langue" placeholder="Français" value={lang.name} onChange={(e) => handleLanguageChange(index, 'name', e.target.value)} className="flex-1" />
                                    <div className="flex-1">
                                        <label className="text-sm font-semibold text-slate-700 ml-1 block mb-2">Niveau ({lang.level}%)</label>
                                        <input type="range" min="0" max="100" value={lang.level} onChange={(e) => handleLanguageChange(index, 'level', parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-600" />
                                    </div>
                                    <button type="button" onClick={() => handleDeleteLanguage(index)} className="text-red-500 hover:text-red-700 sm:mt-6 self-end sm:self-auto">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {templateConfig?.fields.interests && (
                <section className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('interests')}`}>
                    <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-pink-50 rounded-lg shrink-0">
                                <Heart className="w-5 h-5 text-pink-600" />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold text-slate-900">Centres d'intérêt</h3>
                        </div>
                        {!isIconInterests && (
                            <Button onClick={handleAddInterest} className="h-9 px-3 md:px-4 text-xs shrink-0">
                                <Plus className="w-4 h-4 md:mr-2" />
                                <span className="hidden md:inline">Ajouter</span>
                            </Button>
                        )}
                    </div>

                    {isIconInterests ? (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-500">
                                Sur ce modèle, seuls des pictogrammes apparaissent sur le CV.
                                Choisissez jusqu&apos;à {MAGENTA_HOBBY_MAX} icônes
                                {(data.interests?.length || 0) > 0
                                    ? ` (${data.interests!.length}/${MAGENTA_HOBBY_MAX})`
                                    : ''}
                                .
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
                                {MAGENTA_HOBBY_OPTIONS.map((opt) => {
                                    const selected = (data.interests || []).some((i) => i.name === opt.id);
                                    const atMax = (data.interests || []).length >= MAGENTA_HOBBY_MAX && !selected;
                                    const Icon = opt.Icon;
                                    return (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            disabled={atMax}
                                            onClick={() => toggleMagentaHobby(opt.id)}
                                            title={opt.label}
                                            aria-pressed={selected}
                                            className={[
                                                'flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all',
                                                selected
                                                    ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-sm ring-2 ring-pink-200'
                                                    : 'border-slate-200 bg-white text-slate-600 hover:border-pink-300 hover:bg-pink-50/40',
                                                atMax ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                                            ].join(' ')}
                                        >
                                            <span
                                                className={[
                                                    'w-10 h-10 rounded-full flex items-center justify-center',
                                                    selected
                                                        ? 'bg-gradient-to-b from-[#e8334a] to-[#9e1170] text-white'
                                                        : 'bg-slate-100 text-slate-500',
                                                ].join(' ')}
                                            >
                                                <Icon className="w-5 h-5" />
                                            </span>
                                            <span className="text-[10px] font-medium text-center leading-tight line-clamp-2">
                                                {opt.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {(data.interests || []).map((interest, index) => (
                                <div key={interest.id || index} className="flex gap-2 items-center p-3 bg-white border border-slate-200 rounded-lg">
                                    <Input placeholder="Swimming" value={interest.name} onChange={(e) => handleInterestChange(index, e.target.value)} className="flex-1" />
                                    <button type="button" onClick={() => handleDeleteInterest(index)} className="text-red-500 hover:text-red-700">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {templateConfig?.fields.references && (
                <section className={`card-premium ${sectionPad} glass-morphism animate-fade-in ${stepClass('references')}`}>
                    <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-orange-50 rounded-lg shrink-0">
                                <Users className="w-5 h-5 text-orange-600" />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold text-slate-900">Références</h3>
                        </div>
                        <Button onClick={handleAddReference} className="h-9 px-3 md:px-4 text-xs shrink-0">
                            <Plus className="w-4 h-4 md:mr-2" />
                            <span className="hidden md:inline">Ajouter</span>
                        </Button>
                    </div>
                    <div className="space-y-4">
                        {(data.references || []).map((ref, index) => (
                            <div key={ref.id || index} className="p-4 bg-white border border-slate-200 rounded-lg">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                    <Input label="Nom complet" placeholder="John Doe" value={ref.name} onChange={(e) => handleReferenceChange(index, 'name', e.target.value)} />
                                    <Input label="Poste" placeholder="Manager" value={ref.position} onChange={(e) => handleReferenceChange(index, 'position', e.target.value)} />
                                    <Input label="Entreprise" placeholder="Google" value={ref.company} onChange={(e) => handleReferenceChange(index, 'company', e.target.value)} />
                                    <Input label="Téléphone" placeholder="+123 456 7890" value={ref.phone} onChange={(e) => handleReferenceChange(index, 'phone', e.target.value)} />
                                    <Input label="Email" placeholder="john@example.com" value={ref.email} onChange={(e) => handleReferenceChange(index, 'email', e.target.value)} className="sm:col-span-2" />
                                </div>
                                <div className="flex justify-end">
                                    <button type="button" onClick={() => handleDeleteReference(index)} className="text-red-500 hover:text-red-700 text-xs font-bold flex items-center gap-1">
                                        <Trash2 className="w-3 h-3" />
                                        Supprimer
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Navigation étapes — mobile uniquement */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-20 no-print border-t border-slate-200 bg-white/95 backdrop-blur-sm px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3">
                <Button
                    variant="outline"
                    className="h-11 flex-1 text-sm"
                    onClick={goPrev}
                    disabled={isFirst}
                >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Précédent
                </Button>
                {!isLast ? (
                    <Button className="h-11 flex-1 text-sm" onClick={goNext}>
                        Suivant
                        <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                ) : (
                    <div className="flex-1 text-center text-xs font-semibold text-indigo-700 leading-snug px-1">
                        Terminé — ouvrez l’onglet Aperçu
                    </div>
                )}
            </div>
        </div>
    );
};

export default CVForm;
